import { useEffect, useRef } from "react";

import { useOptionalVariantContext } from "~/components/products/variant-selector.components";
import type { C_ProductInfo, Inventory, PathRecord } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { useRootLayoutData } from "~/root";
import { getVariantImage } from "~/shop/utils/image";
import { resolveImageHost } from "~/utils/salesforce-image-transform";

import { trackPageEvent } from "./utils";
import { buildSegmentViewItem } from "./view-item";

// `C_ProductInfo` omits the top-level `price`; the runtime SCAPI product carries
// it (and the sparse `c_*` attrs below), so re-add them for this read.
type TrackerProduct = SerializeFrom<C_ProductInfo> & {
  price?: number;
  c_parentMasterProductID?: string;
  c_marketing_sensitive?: boolean;
};

/**
 * Fires the Segment `View Item` event (SFCC-11012, ANALYTICS-16/-17) on PDP
 * load and re-fires on variant change. Rendered inside the lifted
 * `VariantSelector.SelectedVariant` render prop, which supplies the resolved
 * `variant` + sales `price`; per-variant stock comes from the Selector
 * context's `resolvedVariantData`. Returns null — side effect only.
 *
 * Keyed on the effective SKU (`variant.productId ?? product.id`) via a ref so a
 * quantity change or unrelated re-render doesn't re-fire, but selecting a
 * different variant does. `trackPageEvent` owns the ready-wait +
 * `device`/`sales_channel` decoration (shared with View Category, SFCC-11014).
 *
 * `categories` is the deferred category breadcrumb (a Promise) — it's resolved
 * inside the effect rather than via a render-side `<Await>`, so the analytics
 * fire isn't coupled to a Suspense boundary. A plain array is also accepted.
 */
export const SegmentViewItemTracker = ({
  product,
  categories,
  siteId,
  variant,
  price,
}: {
  product: TrackerProduct;
  categories: PathRecord[] | Promise<PathRecord[]>;
  siteId: string;
  variant?: { productId: string; orderable?: boolean } | undefined;
  /** Resolved sales price from the SelectedVariant render prop (salePrice ?? currentPrice). */
  price?: number | undefined;
}) => {
  const ctx = useOptionalVariantContext();
  const selected = ctx?.selected;
  const resolvedVariantData = ctx?.resolvedVariantData ?? null;
  const { IMAGES_HOST } = useRootLayoutData();
  const firedFor = useRef<string | null>(null);

  const sku = variant?.productId ?? product.id;

  useEffect(() => {
    if (!sku || firedFor.current === sku) return;
    // Set synchronously before tracking so a concurrent effect re-run for the
    // same SKU can't fire a second event.
    firedFor.current = sku;

    // SFRA `getMasterAndVariantID` (:596-626): master id for masters/variants,
    // "" for standalone (standard/bundle/set/item).
    const isVariant = Boolean(variant) || product.type?.variant === true;
    const hasMaster =
      isVariant ||
      product.type?.master === true ||
      Boolean(product.variationAttributes?.length);
    const masterId = hasMaster
      ? (product.c_parentMasterProductID ?? product.id)
      : "";

    // Raw SCAPI `c_priceInfo` is nested under `.priceInfo` (verified against
    // live product data). The render-prop `price` is preferred (it resolves
    // the selected variant's sale/current price); fall back to the product's
    // own sales value, then the top-level `price`.
    const salesInfo = product.c_priceInfo?.priceInfo?.sales;
    const resolvedPrice =
      typeof price === "number" ? price : (salesInfo?.value ?? product.price);

    const variantInventory =
      variant && resolvedVariantData
        ? (resolvedVariantData[variant.productId]?.inventory as
            | Inventory
            | undefined)
        : undefined;
    const masterInventory = product.inventory as Inventory | undefined;
    const stockLevel = variantInventory?.ats ?? masterInventory?.ats;

    // Re-host onto IMAGES_HOST — the raw SCAPI link (instance/realm host) 403s,
    // so the bare link would ship a broken image URL into the Segment payload.
    const imageUrl = resolveImageHost(
      getVariantImage(product as C_ProductInfo, selected, "large")?.[0]?.link,
      IMAGES_HOST,
    );

    let cancelled = false;
    let cancelTrack: (() => void) | undefined;

    // Resolve the deferred category breadcrumb off the render path. Best-effort:
    // a rejected/failed category fetch still fires View Item with empty levels
    // (SFRA parity — categories are non-critical to the event). The cleanup
    // cancels both a still-pending resolve and `trackPageEvent`'s ready-wait.
    void Promise.resolve(categories)
      .catch(() => [] as PathRecord[])
      .then(cats => {
        if (cancelled) return;
        cancelTrack = trackPageEvent(
          "View Item",
          buildSegmentViewItem({
            sku,
            masterId,
            name: product.name,
            brand: product.brand,
            price: resolvedPrice,
            currency: salesInfo?.currency,
            stockLevel,
            marketingSensitive: product.c_marketing_sensitive,
            imageUrl,
            categories: cats ?? [],
            sourceOrigin: "PDP",
          }),
          siteId,
        );
      });

    return () => {
      cancelled = true;
      cancelTrack?.();
    };
  }, [
    sku,
    price,
    product,
    categories,
    siteId,
    variant,
    resolvedVariantData,
    selected,
    IMAGES_HOST,
  ]);

  return null;
};
